// BM-H80 P2 — EngineResultView.
//
// Renders the wills-engine EngineResultT surface: HARD_STOPs (red
// callouts, block submission), advisories (amber callouts), and the
// cross-engine LPA advisory as a "consider an LPA" bloc at the end.
// Used both inside the client-facing form (as live feedback under
// each section) and inside the fee-earner review shell (as a
// consolidated panel).

import { AlertCircle, Info, ShieldAlert } from "lucide-react";

import { sectionLabel } from "./wills/sectionRegistry";

const CROSS_ENGINE_LPA_CODE = "lpa_absent_or_unconfirmed_cross_engine_advisory";

function Callout({ tone, icon: Icon, title, children, testid }) {
  const palette = {
    red: { bg: "#fef2f2", border: "#fecaca", accent: "#b91c1c" },
    amber: { bg: "#fffbeb", border: "#fde68a", accent: "#b45309" },
    slate: { bg: "#f8fafc", border: "#cbd5e1", accent: "#334155" },
  }[tone];
  return (
    <div
      data-testid={testid}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: 12,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 8,
        marginBottom: 12,
      }}
    >
      <Icon size={18} color={palette.accent} style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ fontSize: 14, lineHeight: 1.5 }}>
        {title && (
          <div style={{ fontWeight: 600, marginBottom: 4, color: palette.accent }}>
            {title}
          </div>
        )}
        <div>{children}</div>
      </div>
    </div>
  );
}

// BM-H4005: the engines' hard stops do not carry `message` and `section`. Wills hard stops are
// {code, triggered_at_section, reason, routing} and LPA hard stops are {code, reason, path} (read off the vendored bundles
// and a real engine run, not guessed). Warnings and advisories DO carry `message` and `section`. This used to read only
// message and section for hard stops, so the red box showed a code and no sentence. `message` and `section` are still read
// as a fallback so a stop in the older shape is not blanked.
export function hardStopSentence(hs) {
  return hs.reason || hs.message || "This needs a fee earner's attention.";
}

export function hardStopSection(hs) {
  return hs.triggered_at_section || hs.section || hs.path || "";
}

// BM-H4056 - the LPA engine returns a flat { hard_stops[], advisories[] }; this view reads the
// wills shape { flags: { hard_stops[], warnings[] } }. Fed the flat shape it found no flags and
// printed "No blocking issues found so far" over whatever the engine had raised. The client LPA
// page already converted it (ClientFormLPA.jsx); the staff review page did not. A result already
// in the flags shape is returned unchanged.
export function toEngineViewShape(result) {
  if (!result) return null;
  if (result.flags) return result;
  if (!("hard_stops" in result) && !("advisories" in result)) return result;
  return {
    flags: {
      hard_stops: result.hard_stops || [],
      warnings: (result.advisories || []).map((a) => ({
        code: a.code,
        message: a.message || a.reason,
        section: a.path || a.section || "",
      })),
    },
  };
}

// BM-H4141: hideCodes is for a page the client reads. The rule code is a reference for staff, and on the
// client's screen it was the heading of every amber note.
// eslint-disable-next-line react/prop-types
export default function EngineResultView({ engineResult, compact = false, hideCodes = false }) {
  if (!engineResult) {
    return (
      <div
        data-testid="engine-result-empty"
        style={{ fontSize: 13, color: "#64748b", padding: "8px 0" }}
      >
        Live checks will appear here as you fill in the form.
      </div>
    );
  }

  const flags = engineResult.flags || {};
  const hardStops = flags.hard_stops || [];
  const warnings = flags.warnings || [];
  const crossEngine = warnings.find((w) => w.code === CROSS_ENGINE_LPA_CODE);
  const otherWarnings = warnings.filter((w) => w.code !== CROSS_ENGINE_LPA_CODE);

  return (
    <div data-testid="engine-result-view">
      {hardStops.length > 0 && (
        <div data-testid="engine-hard-stops">
          {hardStops.map((hs, idx) => (
            <Callout
              key={`hs-${idx}`}
              tone="red"
              icon={ShieldAlert}
              title="Needs attention"
              testid={`engine-hard-stop-${hs.code || idx}`}
            >
              {hardStopSentence(hs)}
              {hardStopSection(hs) && (
                <div style={{ fontSize: 12, color: "#7f1d1d", marginTop: 4 }}>
                  Section: {sectionLabel(hardStopSection(hs))}
                </div>
              )}
              {hs.code && !hideCodes && (
                <div data-testid={`engine-hard-stop-ref-${hs.code}`} style={{ fontSize: 11, color: "#7f1d1d", marginTop: 2 }}>
                  Reference: {hs.code}
                </div>
              )}
            </Callout>
          ))}
        </div>
      )}

      {otherWarnings.length > 0 && (
        <div data-testid="engine-warnings">
          {otherWarnings.map((w, idx) => (
            <Callout
              key={`w-${idx}`}
              tone="amber"
              icon={AlertCircle}
              title={w.title || (hideCodes ? "Please check" : w.code) || "Advisory"}
              testid={`engine-warning-${w.code || idx}`}
            >
              {w.message}
            </Callout>
          ))}
        </div>
      )}

      {crossEngine && !compact && (
        <Callout
          tone="slate"
          icon={Info}
          title="Consider an LPA"
          testid="engine-cross-engine-lpa-advisory"
        >
          {crossEngine.message}
        </Callout>
      )}

      {hardStops.length === 0 && warnings.length === 0 && (
        <div
          data-testid="engine-result-clean"
          style={{ fontSize: 13, color: "#166534", padding: "8px 0" }}
        >
          No blocking issues found so far.
        </div>
      )}
    </div>
  );
}
